import { useEffect } from 'react'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import {
  useHasFeature,
  useOrganizationEntitlements,
  useOrganizationPosters,
} from '@workspace/react-query'
import { Skeleton } from '@workspace/ui/components/skeleton'
import { authClient } from '@/lib/auth'
import { requireActiveSubscription } from '@/lib/middleware'
import { useTopNav } from '@/context/top-nav-provider'
import { Main } from '@/components/layout/main'
import {
  OrganizationPostersPage,
  type PosterLink,
} from '@/features/houses/library/organization-posters-page'

export const Route = createFileRoute(
  '/_authenticated/_organization/houses/library/posters'
)({
  beforeLoad: async () => requireActiveSubscription(),
  component: OrganizationPostersRoute,
})

/**
 * The organisation's posters: not one house's page, so it has its own crumbs.
 * A poster opens at a house (its page shows that house's places and print), so
 * each row links to the poster at the first house the viewer reaches where it
 * has a place, falling back to any house in the list; a poster with no house
 * to open it at links to the Houses list.
 */
function OrganizationPostersRoute() {
  const { data: organization } = authClient.useActiveOrganization()
  const organizationId = organization?.id ?? ''
  const { setLinks } = useTopNav()
  const entitlements = useOrganizationEntitlements(organizationId)
  const hasLibrary = useHasFeature(organizationId, 'sil.library')
  // The page reads the same list (one cache entry).
  const posters = useOrganizationPosters(organizationId)

  useEffect(() => {
    setLinks([
      { title: 'Houses', href: '/houses' },
      {
        title: 'Posters and signs',
        href: '/houses/library/posters',
        isActive: true,
      },
    ])
    return () => setLinks([])
  }, [setLinks])

  const list = posters.data?.data?.posters ?? []
  const anyHouseId =
    list.flatMap((poster) => poster.houses)[0]?.facilityId ?? null
  const posterLink = (posterId: string): PosterLink => {
    const houses = list.find((poster) => poster.id === posterId)?.houses ?? []
    const houseId =
      (houses.find((house) => house.status !== 'missing') ?? houses[0])
        ?.facilityId ?? anyHouseId
    return houseId
      ? {
          to: '/houses/$houseId/library/posters/$posterId',
          params: { houseId, posterId },
        }
      : { to: '/houses', params: {} }
  }

  let body
  if (!organizationId || entitlements.isPending) {
    body = <Skeleton role='status' aria-label='Loading' className='h-64' />
  } else if (!hasLibrary) {
    // The Library is behind the pilot key: without it the Houses page is the way back.
    body = <Navigate to='/houses' replace />
  } else {
    body = <OrganizationPostersPage posterLink={posterLink} />
  }
  return <Main fluid>{body}</Main>
}
